import { CheckCircle2, Layers, Target, Dumbbell, TrendingUp, Smartphone, Grid3X3, Zap } from 'lucide-react';
import { ImageUpload } from '../analysis/ImageUpload';

export function LandingPage() {
  return (
    <>
      <div className="pt-12 sm:pt-20 pb-8 sm:pb-12 text-center px-4">
        {/* Hero */}
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-400 border border-emerald-500/20 mb-6">
          Your coach in the cloud
        </div>

        <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold tracking-tight mb-4 sm:mb-6 leading-tight">
          Know Exactly<br />
          <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">
            What to Fix.
          </span>
        </h1>

        <p className="text-base sm:text-xl text-slate-300 mb-8 sm:mb-10 max-w-2xl mx-auto">
          Upload 1-7 swing photos and get the
          <br className="hidden sm:block" />
          {' '}specific fixes that will actually lower your scores.
        </p>

        {/* Upload CTA */}
        <div className="max-w-2xl mx-auto mb-8 sm:mb-12">
          <ImageUpload />
        </div>

        {/* Trust */}
        <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-6 lg:gap-8 text-xs sm:text-sm text-slate-400 mb-12">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <span>Frame-by-frame breakdown</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <span>10-category scoring + handicap estimate</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <span>One critical fix with a drill</span>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-16 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl sm:text-4xl font-bold mb-4 text-center">
            How It Works
          </h2>
          <p className="text-slate-400 text-center mb-10 max-w-2xl mx-auto">
            No app to download. Works on any phone or computer.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-6 w-6 text-emerald-400" />
              </div>
              <div className="text-emerald-400 text-sm font-bold mb-2">Step 1</div>
              <h3 className="text-lg font-bold text-white mb-2">Upload Photos</h3>
              <p className="text-slate-400 text-sm">1-7 swing photos from any angle. Screenshots from video work great too.</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Grid3X3 className="h-6 w-6 text-emerald-400" />
              </div>
              <div className="text-emerald-400 text-sm font-bold mb-2">Step 2</div>
              <h3 className="text-lg font-bold text-white mb-2">AI Analyzes Each Frame</h3>
              <p className="text-slate-400 text-sm">Birdie identifies each swing phase and breaks down body positions, angles, and sequencing.</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Zap className="h-6 w-6 text-emerald-400" />
              </div>
              <div className="text-emerald-400 text-sm font-bold mb-2">Step 3</div>
              <h3 className="text-lg font-bold text-white mb-2">Get Your Fixes</h3>
              <p className="text-slate-400 text-sm">10-category scoring, handicap estimate, ranked issues, and one critical fix with a drill.</p>
            </div>
          </div>
        </div>
      </div>

      {/* What You Get */}
      <div className="py-16 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl sm:text-4xl font-bold mb-10 text-center">
            What You Get
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <Layers className="h-6 w-6 text-emerald-400 mb-3" />
              <h3 className="text-lg font-bold text-white mb-2">Frame-by-Frame Breakdown</h3>
              <p className="text-slate-400 text-sm">Every photo analyzed for body positions, angles, and what they mean for your ball flight.</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <Target className="h-6 w-6 text-emerald-400 mb-3" />
              <h3 className="text-lg font-bold text-white mb-2">Ranked Issues</h3>
              <p className="text-slate-400 text-sm">Not 20 things to think about. Issues ranked by impact with root cause explanations.</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <Dumbbell className="h-6 w-6 text-emerald-400 mb-3" />
              <h3 className="text-lg font-bold text-white mb-2">One Critical Fix + Drill</h3>
              <p className="text-slate-400 text-sm">The single change that will help most, with a specific drill you can do right now.</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <TrendingUp className="h-6 w-6 text-emerald-400 mb-3" />
              <h3 className="text-lg font-bold text-white mb-2">10-Category Scoring</h3>
              <p className="text-slate-400 text-sm">Setup, backswing, transition, impact, and more. See exactly where your swing is strong and weak.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sample Analysis Output */}
      <div className="py-16 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl sm:text-4xl font-bold mb-4 text-center">
            This Is What You Get
          </h2>
          <p className="text-slate-400 text-center mb-10 max-w-2xl mx-auto">
            Real output from a real swing. Yours will be different because it's built around you.
          </p>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8">
            {/* Score Header */}
            <div className="text-center mb-6 pb-6 border-b border-white/10">
              <p className="text-sm text-slate-400 mb-1">SwingScore</p>
              <div className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent mb-1">68</div>
              <p className="text-slate-400">
                Projected Handicap: <span className="text-white font-bold">12-15</span>
              </p>
              <p className="text-slate-500 text-sm mt-1">7-iron</p>
            </div>

            {/* Score Categories */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 mb-6 pb-6 border-b border-white/10">
              {[
                ['Setup', 8], ['Backswing', 7], ['Transition', 5], ['Impact', 7],
                ['Follow-Through', 7], ['Efficiency', 6], ['Tempo', 7],
                ['Repeatability', 6], ['Power', 7], ['Consistency', 6]
              ].map(([name, val]) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-24">{name}</span>
                  <div className="flex-1 h-1.5 bg-white/10 rounded-full">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${val * 10}%` }} />
                  </div>
                  <span className="text-xs text-slate-300 w-4 text-right">{val}</span>
                </div>
              ))}
            </div>

            {/* What's Costing Strokes #1 */}
            <div className="rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 p-4 sm:p-5 mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-bold text-amber-400">#1 Over-the-Top Transition</span>
              </div>
              <p className="text-slate-300 text-sm mb-2">Right shoulder fires toward the ball before the hips clear. Upper body is spinning while the lower body is still static.</p>
              <p className="text-xs text-slate-400 mb-1">Why it matters:</p>
              <p className="text-slate-300 text-sm">Out-to-in path = weak cut/slice. Can't shallow the club when the upper body leads.</p>
            </div>

            {/* Issue #2 */}
            <div className="rounded-xl bg-white/5 border border-white/10 p-4 sm:p-5 mb-4">
              <span className="text-sm font-bold text-slate-400">#2 Early Extension</span>
              <p className="text-slate-300 text-sm mt-2">Hips thrust toward the ball on the downswing instead of rotating in place.</p>
            </div>

            {/* Critical Fix */}
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 mb-4">
              <div className="font-bold text-emerald-400 text-sm mb-1">CRITICAL FIX: Pump Drill</div>
              <p className="text-slate-300 text-sm mb-2">Take the club to the top, pump down halfway without hitting, feel the hip lead. 5 pumps, then hit. Repeat 10 times.</p>
              <p className="text-xs text-slate-400">Success: Hands drop inside, club shallows, path goes from -4 to 0.</p>
            </div>

            {/* Swing Thought */}
            <div className="text-center pt-4 border-t border-white/10">
              <div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Swing Thought</div>
              <div className="text-white font-medium">"Hips go, hands stay"</div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
}
